import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { useQuery } from "seed/gql";
import { parseDatatypes } from "components/collections/util/datatypes";
import { filterData, sortData, getFilteredColumns } from "components/collections/util/filters";
import ColumnsEvents from "components/collections/Table.lib.columns";
import RowsEvents from "components/collections/Table.lib.rows";
import TableEvents from "components/collections/Table.lib.table";
import TableListeners from "components/collections/Table.lib.listeners";
import View from "components/collections/Table.view";

const Table = forwardRef(({
	flow,
  activeCollection,
	primaryKey,
  values,
	rawValues,
  structure,
  pageNum,
  pageSize,
  totalPages,
  query,
  filters,
  sorting,
  search,
  collections = [],
  collectionOptions = {},
  useCustomPagination = false,
  useCustomSearch = false,
  useCustomFilters = false,
  useCustomImport = false,
  useCustomExport = false,
  useCustomSorting = false,
	showForm = false,
  showExport = true,
  showImport = true,
  showOptions = true,
  showSearch = true,
  showFilters = true,
  showSorting = true,
  showAdd = false,
	showDelete = false,
	showPermissions = false,
  showManageCollections,
	checkValidation = false,
	trackKeybindings = true,
  isReadOnly = false,
	isLoading = false,
  onClickCloneCollection = () => {},
  onClickDeleteCollection = () => {},
	onClickClearData = () => {},
  onClickDeleteRow = () => {},
  onClickDeleteMultipleRows = () => {},
  onClickImport = () => {},
  onClickExport = () => {},
  onClickDownloadZip = () => {},
  onClickCell = () => {},
	onDoubleClickCell = () => {},
	onBlurCell = () => {},
	onClickOpenCollectionEditModal = () => {},
	onClickOpenColumnCreateModal = () => {},
	onClickOpenColumnEditModal = () => {},
	onClickOpenPermissionModal = () => {},
  pushColumnOperation = () => {},
  pushRowOperation = () => {},
	updateSearch = () => {},
	updateFilters = () => {},
	updateQuery = () => {},
	updateSorting = () => {},
	updatePage = () => {},
	refetchCollections = () => {}
}, ref) => {

	const cacheRef = useRef({});
	const searchRef = useRef(null);

	const [_rows, setRows] = useState([]);
	const [_columns, setColumns] = useState([]);
	const [_query, setQuery] = useState("");
	const [_search, setSearch] = useState("");
	const [_filters, setFilters] = useState(filters || []);
	const [_sorting, setSorting] = useState(sorting || []);
	const [_pageNum, setPageNum] = useState(1);
	const [_pageSize, setPageSize] = useState(10);
	const [_totalPages, setTotalPages] = useState(1);

	const [selectedFile, setSelectedFile] = useState(null);
	const [filterDefault, setFilterDefault] = useState(null);
	const [isFilterModalShown, setIsFilterModalShown] = useState(false);
	const [isOptionModalShown, setIsOptionModalShown] = useState(false);
	const [isFilePreviewShown, setIsFilePreviewShown] = useState(false);
	const [isSingleImport, setIsSingleImport] = useState(false);
	const [isImportModalShown, setIsImportModalShown] = useState(false);
	const [isExportModalShown, setIsExportModalShown] = useState(false);
	const [isFormModalShown, setIsFormModalShown] = useState(false);
	const [isSearchShown, setIsSearchShown] = useState(false);
	const [isSortingModalShown, setIsSortingModalShown] = useState(false);
	const [isZipModalShown, setIsZipModalShown] = useState(false);
	const [isCloneModalShown, setIsCloneModalShown] = useState(false);
	const [zipFieldName, setZipFieldName] = useState(null);
	const [selectedCell, setSelectedCell] = useState(null);
	const [isEditingCell, setIsEditingcell] = useState(false);
	const [multipleSelectedCells, setMultipleSelectedCells] = useState([]);
	const [formValue, setFormValue] = useState(null);
	const tableRef = useRef(null);

	const qDataTypes = useQuery(`{
		appDatatypes {
			name
			structure
			app {
				name
				version
			}
		}
	}`);

	useEffect(() => {

		const { onKeyUp } = TableListeners({ _rows, _columns, 
			trackKeybindings, selectedCell, setSelectedCell, setIsEditingcell });

		document.addEventListener("keyup", onKeyUp);

		return () => {
			document.removeEventListener("keyup", onKeyUp);
		}

	}, [selectedCell, trackKeybindings, _rows, _columns]);

	useEffect(() => {
		if (selectedFile)
			setIsFilePreviewShown(true);
	}, [selectedFile]);

	useEffect(() => {
		if (values) setRows(values);
	}, [values]);

	useEffect(() => {
		if(!rawValues) return setColumns(structure);
		let { newStructure, newRawValues } = parseDatatypes(structure, rawValues, appDatatypes);
		setColumns(newStructure);
		setRows(newRawValues);
	}, [structure, appDatatypes, rawValues]);

	useEffect(() => {
		if (totalPages != null) setTotalPages(totalPages);
	}, [totalPages]);

	useEffect(() => {
		if (pageNum != null) setPageNum(pageNum);
	}, [pageNum]);

	useEffect(() => {
		if (pageSize != null) setPageSize(pageSize);
	}, [pageSize]);

	useEffect(() => {
		if (query != null) setQuery(query);
	}, [query]);

	useEffect(() => {
		if (search != null) {
			setSearch(search);
			if(searchRef.current) searchRef.current.value = search;
		}
	}, [search]);

	useEffect(() => {
		if (filters != null) setFilters(filters);
	}, [filters]);

	useEffect(() => {
		if (sorting != null) setSorting(sorting);
	}, [sorting]);

	useEffect(() => {
		if (!trackKeybindings) setSelectedCell(null);
	}, [trackKeybindings]);

	useEffect(() => {
		if (_pageNum > _totalPages && _totalPages > 0)
			_updatePage(_totalPages);
	}, [_totalPages]);

	useEffect(() => {
		if(!useCustomPagination) {
			let filteredData = filterData({ data: _rows, search: _search, query: _query, pageNum: _pageNum, 
				pageSize: _pageSize, useSearch: !useCustomSearch, useQuery: !useCustomFilters });
			setTotalPages(filteredData.length > _pageSize ? Math.ceil(filteredData.length / _pageSize) : 1);
		}
	}, [pageNum, totalPages, _rows, _query, _search]);

	useEffect(() => {
		setSelectedCell(null);
	}, [activeCollection]);

	const _updatePage = (page) => {
		if(page < 1) page = 1;
		if(page > _totalPages) page = _totalPages;
		setPageNum(page);
		updatePage(page);
	}

	const { 
		_onClickAddColumn,
    _onClickChangeColumn,
    _onClickMoveLeftColumn,
    _onClickMoveRightColumn,
    _onClickDeleteColumn
	} = ColumnsEvents({
		_columns, 
		_rows, 
		cacheRef, 
		isReadOnly, 
		setColumns, 
		setRows, 
		setSelectedCell, 
		pushColumnOperation 
	});

	const { 
		_onAddRow,
		_onClickDeleteRow,
		_onClickDeleteMultipleRows,
		_onClickInsertRow,
		_onClickCloneRow,
		_onChangeRow
	} = RowsEvents({
		_columns,
		_rows, 
		_pageNum, 
		_pageSize, 
		_totalPages, 
		cacheRef, 
		selectedCell, 
		isReadOnly, 
		showAdd, 
		showDelete, 
		showForm, 
		checkValidation, 
		setRows, 
		setSelectedCell, 
		onClickDeleteRow,
		onClickDeleteMultipleRows,
		pushRowOperation, 
		_updatePage 
	});

	const { 
		_onClickImport,
		_onClickExport,
		_onClickCell,
		_onDoubleClickCell,
		_onBlurCell,
		_onClickSubmitForm,
		_updateSearch,
		_updateQuery,
		_updateFilter,
		_updateSorting,
	} = TableEvents({
		_rows, 
		_columns, 
		collectionOptions,
		cacheRef, 
		isReadOnly, 
		useCustomImport, 
		useCustomExport, 
		formValue, 
		setIsFormModalShown,
		setRows, 
		setSearch, 
		setQuery, 
		setFilters, 
		setSorting, 
		setSelectedCell, 
		setIsEditingcell,
		onClickImport, 
		onClickExport, 
		onClickCell, 
		onDoubleClickCell,
		onBlurCell,
		updateSearch,
		updateQuery,
		updateFilters,
		updateSorting,
		_onAddRow, 
		_onChangeRow,
	});

	const { appDatatypes } = qDataTypes.data || {};

	const filteredData = filterData({ data: _rows, search: _search, query: _query, pageNum: _pageNum, pageSize: _pageSize, 
		useSearch: !useCustomSearch, useQuery: !useCustomFilters, usePagination: !useCustomPagination });
	const sortedData = sortData({ data: filteredData, sorting: _sorting, useSorting: !useCustomSorting });

	const filteredColumns = getFilteredColumns(_filters);

	useImperativeHandle(ref, () => ({
		cleanSelection: () => {
			setSelectedCell(null);
			setMultipleSelectedCells([]);
		},
		showImport,
		showExport,
		showManageCollections,
		showPermissions,
		isReadOnly,
		setIsImportModalShown,
    setIsExportModalShown,
    setIsCloneModalShown,
    onClickOpenCollectionEditModal,
    onClickOpenPermissionModal,
    onClickDeleteCollection,
	}));

	return <View
		tableRef={tableRef}
		searchRef={searchRef}
		flow={flow}
		activeCollection={activeCollection}
		collections={collections}
		collectionOptions={collectionOptions}
		rows={sortedData}
		columns={_columns}
		primaryKey={primaryKey}
		filterDefault={filterDefault}
		zipFieldName={zipFieldName}
		filteredData={filteredData}
		filteredColumns={filteredColumns}
		formValue={formValue}
		selectedCell={selectedCell}
		selectedFile={selectedFile}
		multipleSelectedCells={multipleSelectedCells}
		pageNum={_pageNum}
		totalPages={_totalPages}
		query={_query}
		filters={_filters}
		search={_search}
		sorting={_sorting}
		showFilters={showFilters}
		showOptions={showOptions}
		showImport={showImport}
		showExport={showExport}
		showSearch={showSearch}
		showSorting={showSorting}
		showForm={showForm}
		showAdd={showAdd}
		showDelete={showDelete}
		showPermissions={showPermissions}
		showManageCollections={showManageCollections}
		isReadOnly={isReadOnly}
		isLoading={isLoading}
		isFilePreviewShown={isFilePreviewShown}
		isFilterModalShown={isFilterModalShown}
		isOptionModalShown={isOptionModalShown}
		isImportModalShown={isImportModalShown}
		isSingleImport={isSingleImport}
		isExportModalShown={isExportModalShown}
		isFormModalShown={isFormModalShown}
		isSearchShown={isSearchShown}
		isSortingModalShown={isSortingModalShown}
		isZipModalShown={isZipModalShown}
		isCloneModalShown={isCloneModalShown}
		isEditingCell={isEditingCell}
		setSelectedCell={setSelectedCell}
		setSelectedFile={setSelectedFile}
		setMultipleSelectedCells={setMultipleSelectedCells}
		setFormValue={setFormValue}
		setIsFilePreviewShown={setIsFilePreviewShown}
		setIsFilterModalShown={setIsFilterModalShown}
		setIsOptionModalShown={setIsOptionModalShown}
		setIsSingleImport={setIsSingleImport}
		setIsImportModalShown={setIsImportModalShown}
		setIsExportModalShown={setIsExportModalShown}
		setIsFormModalShown={setIsFormModalShown}
		setIsSearchShown={setIsSearchShown}
		setFilterDefault={setFilterDefault}
		setIsSortingModalShown={setIsSortingModalShown}
		setIsZipModalShown={setIsZipModalShown}
		setIsCloneModalShown={setIsCloneModalShown}
		setZipFieldName={setZipFieldName}
		onClickChangeColumn={_onClickChangeColumn}
		onClickMoveLeftColumn={_onClickMoveLeftColumn}
		onClickMoveRightColumn={_onClickMoveRightColumn}
		onClickDeleteColumn={_onClickDeleteColumn}
		onAddRow={_onAddRow}
		onClickDeleteRow={_onClickDeleteRow}
		onClickDeleteMultipleRows={_onClickDeleteMultipleRows}
		onClickInsertRow={_onClickInsertRow}
		onClickCloneRow={_onClickCloneRow}
		onChangeRow={_onChangeRow}
		onClickImport={_onClickImport}
		onClickExport={_onClickExport}
		onClickDownloadZip={onClickDownloadZip}
		onClickCell={_onClickCell}
		onDoubleClickCell={_onDoubleClickCell}
		onBlurCell={_onBlurCell}
		onClickSubmitForm={_onClickSubmitForm}
		onClickDeleteCollection={onClickDeleteCollection}
		onClickClearData={onClickClearData}
		onClickCloneCollection={onClickCloneCollection}
		onClickOpenCollectionEditModal={onClickOpenCollectionEditModal}
		onClickOpenColumnCreateModal={onClickOpenColumnCreateModal}
		onClickOpenColumnEditModal={onClickOpenColumnEditModal}
		onClickOpenPermissionModal={onClickOpenPermissionModal}
		updateSearch={_updateSearch}
		updateFilters={_updateFilter}
		updateQuery={_updateQuery}
		updateSorting={_updateSorting}
		updatePage={_updatePage}
		refetchCollections={refetchCollections}
	/>

});

Table.displayName = "Table";

export default Table;